"use client";

import { useMemo } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";
import { Label, cn } from "@fluid-app/ui-primitives";
import { Combobox } from "@fluid-app/ui-components/components/Combobox";

type NameValue = { name: string; value: string | number };

interface FormComboboxFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  label?: string;
  placeholder?: string;
  emptyText?: string;
  options?: NameValue[];
  containerClassName?: string;
  disabled?: boolean;
}

export function FormComboboxField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder = "Select",
  emptyText = "No results found.",
  options,
  containerClassName,
  disabled,
}: FormComboboxFieldProps<TFieldValues, TName>) {
  const comboboxOptions = useMemo(
    () =>
      options?.map((opt) => ({
        label: opt.name,
        value: opt.value.toString(),
      })) ?? [],
    [options],
  );

  return (
    <div className={cn("space-y-1", containerClassName)}>
      {label && (
        <Label htmlFor={name} className="mb-1.5 block text-sm font-medium">
          {label}
        </Label>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <Combobox
              options={comboboxOptions}
              value={value?.toString() ?? ""}
              onValueChange={onChange}
              placeholder={placeholder}
              emptyText={emptyText}
              disabled={disabled}
            />
            {error?.message && (
              <p className="text-destructive text-sm">{error.message}</p>
            )}
          </>
        )}
      />
    </div>
  );
}
